import React from 'react';
import { Box, LinearProgress, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardIos';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  OperationProcessService,
  computeOperationProgress,
  findNextOperationStep,
} from '../../../services/OperationProcessService';

/**
 * 사이트 별 운영 프로세스 진행률 + 다음 작업 배지.
 *
 * - GET /v0.1/site-management/projects/:id/operation-process (React Query 캐시).
 * - 진행률: (checked 부모+자식) / (전체 부모+자식).
 * - 다음 작업: findNextOperationStep 의 결과 — 미체크 행 중 첫 항목 이름.
 * - 클릭 시 /project/:siteId/operation-process 로 이동. row onClick 전파 막음.
 */
interface Props {
  siteId: string | number;
}

const OperationProgressBadge: React.FC<Props> = ({ siteId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const numericId = Number(siteId);
  const enabled = Number.isFinite(numericId) && numericId > 0;
  const { data, isLoading } = useQuery({
    queryKey: ['operation-process', 'by-site', numericId],
    queryFn: () => OperationProcessService.getBySite(numericId),
    enabled,
    staleTime: 60_000,
    retry: false,
  });

  const handleClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (!enabled) return;
    navigate(`/project/${siteId}/operation-process`);
  };

  if (!enabled) return <Typography variant="caption" color="text.secondary">—</Typography>;
  if (isLoading) {
    return (
      <Typography variant="caption" color="text.secondary">
        로딩 중...
      </Typography>
    );
  }

  const progress = computeOperationProgress(data ?? null);

  // 운영 프로세스 자체가 비어 있으면 — 작성 페이지로 가도록 같은 클릭 동작 유지.
  if (progress.total === 0) {
    return (
      <Box
        onClick={handleClick}
        role="button"
        tabIndex={0}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          cursor: 'pointer',
          color: theme.palette.text.secondary,
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main },
        }}
      >
        <Typography variant="caption">미작성</Typography>
        <ArrowForwardIcon sx={{ fontSize: 12, opacity: 0.7 }} />
      </Box>
    );
  }

  const color =
    progress.pct >= 100 ? 'success' : progress.pct >= 50 ? 'info' : 'warning';
  const next = findNextOperationStep(data ?? null);

  const nextLine =
    progress.pct >= 100 || !next
      ? { label: '완료', muted: true }
      : { label: `다음: ${next.label}`, muted: false };

  return (
    <Tooltip
      title={
        next
          ? `${progress.done} / ${progress.total} (${progress.pct}%) · 다음: ${next.label} — 클릭하여 운영 프로세스로 이동`
          : `${progress.done} / ${progress.total} (${progress.pct}%) — 클릭하여 운영 프로세스로 이동`
      }
    >
      <Box
        onClick={handleClick}
        role="button"
        tabIndex={0}
        sx={{
          minWidth: 180,
          maxWidth: 260,
          px: 0.75,
          py: 0.5,
          borderRadius: 1,
          cursor: 'pointer',
          transition: 'background-color 120ms ease',
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
          >
            {progress.pct}%
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            ({progress.done}/{progress.total})
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progress.pct}
          color={color}
          sx={{ height: 4, borderRadius: 2, mb: 0.5 }}
        />
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: nextLine.muted ? 'success.main' : 'text.primary',
              fontWeight: nextLine.muted ? 600 : 500,
            }}
          >
            {nextLine.label}
          </Typography>
          <ArrowForwardIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
        </Stack>
      </Box>
    </Tooltip>
  );
};

export default OperationProgressBadge;
